import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  TrendingUp, GraduationCap, DollarSign, Share2, Linkedin, Twitter, Quote, ArrowRight,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import SeoHead from '@/components/shared/SeoHead';
import PageHero from '@/components/shared/PageHero';
import NewsletterSignup from '@/components/shared/NewsletterSignup';
import { LeaderboardAd, InContentAd } from '@/components/shared/AdSense';
import { supabase } from '@/integrations/supabase/client';
import { toTitleCase } from '@/lib/utils';
import { generateSalaryPageUrl } from '@/lib/occupationUtils';
import {
  generateArticleSchema, generateDatasetSchema, generateMultipleSchemas,
} from '@/lib/schema';

// Latest OEWS reference year powering the rankings below.
const DATA_YEAR = 2025;

interface TopOccupation {
  occ_title?: string;
  soc?: string;
  annual_mean_wage?: number;
  annual_median_wage?: number;
  total_employment?: number;
  no_degree_flag?: boolean;
}

interface TrendingCareer {
  soc_code?: string;
  code?: string;
  title?: string;
  name?: string;
  averageSalary?: number;
  growth?: string;
  growthNumeric?: number;
}

const formatCurrency = (value?: number) =>
  value
    ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(Math.round(value))
    : 'N/A';

const getEdition = () => {
  const now = new Date();
  const quarter = Math.floor(now.getMonth() / 3) + 1;
  return { label: `Q${quarter} ${now.getFullYear()} Edition`, iso: now.toISOString().split('T')[0] };
};

const StateOfWages = () => {
  const baseUrl = 'https://wageatlas.com';
  const pageUrl = `${baseUrl}/state-of-american-wages/`;
  const edition = useMemo(getEdition, []);

  const { data: highPaying } = useQuery({
    queryKey: ['sow-high-paying'],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke('api-top-occupations', {
        body: { area: 'United States', type: 'high_paying', limit: 10, year: String(DATA_YEAR) },
      });
      if (error) throw error;
      return (data?.occupations || []) as TopOccupation[];
    },
    staleTime: 1000 * 60 * 60 * 12,
  });

  const { data: noDegree } = useQuery({
    queryKey: ['sow-no-degree'],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke('api-top-occupations', {
        body: { area: 'United States', type: 'no_degree', limit: 10, year: String(DATA_YEAR) },
      });
      if (error) throw error;
      return (data?.occupations || []) as TopOccupation[];
    },
    staleTime: 1000 * 60 * 60 * 12,
  });

  const { data: trending } = useQuery({
    queryKey: ['sow-trending'],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke('api-trending-careers');
      if (error) throw error;
      return (data || []) as TrendingCareer[];
    },
    staleTime: 1000 * 60 * 60 * 12,
  });

  const topPaying = highPaying?.[0];
  const topNoDegree = noDegree?.[0];

  const citation = `Wage Atlas. "The State of American Wages (${edition.label})." Wage Atlas, ${edition.iso}. ${pageUrl}`;

  const handleCopyCitation = async () => {
    try {
      await navigator.clipboard.writeText(citation);
      toast.success('Citation copied');
    } catch {
      toast.error('Could not copy. Select the text manually.');
    }
  };

  const shareText = encodeURIComponent('The State of American Wages - highest-paying jobs, no-degree careers and fastest-growing occupations, from official BLS data:');
  const shareUrl = encodeURIComponent(pageUrl);

  const schemas = generateMultipleSchemas([
    generateArticleSchema(
      `The State of American Wages - ${edition.label}`,
      'A recurring, data-driven report on the highest-paying jobs, top no-degree careers and fastest-growing occupations in America, built on official BLS and BEA data.',
      pageUrl,
      baseUrl,
      edition.iso,
    ),
    generateDatasetSchema(
      'The State of American Wages',
      'Rankings of U.S. occupations by pay, no-degree accessibility and projected growth, derived from BLS OEWS and Employment Projections data.',
      baseUrl,
      { keywords: ['salary report', 'highest paying jobs', 'no degree jobs', 'wage data', 'BLS'] },
    ),
  ]);

  const renderRankingTable = (rows: TopOccupation[] | undefined, valueLabel: string) => (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b text-left text-muted-foreground">
            <th className="py-2 pr-2 font-medium">#</th>
            <th className="py-2 pr-2 font-medium">Occupation</th>
            <th className="py-2 pr-2 font-medium text-right">{valueLabel}</th>
            <th className="py-2 font-medium text-right hidden sm:table-cell">Jobs</th>
          </tr>
        </thead>
        <tbody>
          {(rows || []).slice(0, 10).map((row, i) => (
            <tr key={`${row.soc}-${i}`} className="border-b last:border-0 hover:bg-muted/40">
              <td className="py-2 pr-2 text-muted-foreground">{i + 1}</td>
              <td className="py-2 pr-2">
                <Link
                  to={generateSalaryPageUrl({ title: row.occ_title || '', soc: row.soc }, 'united-states')}
                  className="text-wage-primary hover:underline font-medium"
                >
                  {toTitleCase(row.occ_title || 'Unknown')}
                </Link>
              </td>
              <td className="py-2 pr-2 text-right font-semibold">{formatCurrency(row.annual_mean_wage)}</td>
              <td className="py-2 text-right text-muted-foreground hidden sm:table-cell">
                {row.total_employment ? row.total_employment.toLocaleString() : '-'}
              </td>
            </tr>
          ))}
          {(!rows || rows.length === 0) && (
            <tr><td colSpan={4} className="py-6 text-center text-muted-foreground">Loading rankings...</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );

  return (
    <>
      <SeoHead
        title={`The State of American Wages - ${edition.label} | Wage Atlas`}
        description="Our recurring report on American pay: the highest-paying jobs, the best careers without a degree, and the fastest-growing occupations - all from official BLS and BEA data. Free to read and cite."
        canonical={pageUrl}
        schemas={schemas}
      />

      <div className="min-h-screen bg-background">
        {/* Hero */}
        <PageHero
          badge={{ label: <>{edition.label} &middot; Updated quarterly</> }}
          title="The State of American Wages"
          subtitle="A plain-English, data-driven look at what America actually earns - the highest-paying jobs, the best careers without a degree, and the fastest-growing occupations. Built entirely on official government data, free to read and cite."
          maxWidth="max-w-3xl"
        />

        <div className="container mx-auto max-w-4xl px-4 py-10 space-y-12">
          <LeaderboardAd />

          {/* Key findings */}
          <section>
            <h2 className="text-2xl font-bold mb-4">Key findings this quarter</h2>
            <div className="grid sm:grid-cols-3 gap-4">
              <Card>
                <CardContent>
                  <DollarSign className="h-6 w-6 text-wage-primary mb-2" />
                  <div className="text-sm text-muted-foreground">Highest-paying occupation</div>
                  <div className="font-semibold">{topPaying ? toTitleCase(topPaying.occ_title || '') : '...'}</div>
                  <div className="text-wage-primary font-bold">{formatCurrency(topPaying?.annual_mean_wage)}</div>
                </CardContent>
              </Card>
              <Card>
                <CardContent>
                  <GraduationCap className="h-6 w-6 text-wage-primary mb-2" />
                  <div className="text-sm text-muted-foreground">Top job without a degree</div>
                  <div className="font-semibold">{topNoDegree ? toTitleCase(topNoDegree.occ_title || '') : '...'}</div>
                  <div className="text-wage-primary font-bold">{formatCurrency(topNoDegree?.annual_mean_wage)}</div>
                </CardContent>
              </Card>
              <Card>
                <CardContent>
                  <TrendingUp className="h-6 w-6 text-wage-primary mb-2" />
                  <div className="text-sm text-muted-foreground">Fastest-growing (featured)</div>
                  <div className="font-semibold">
                    {trending?.[0] ? (trending[0].title || trending[0].name) : '...'}
                  </div>
                  <div className="text-wage-primary font-bold">{trending?.[0]?.growth || ''}</div>
                </CardContent>
              </Card>
            </div>
          </section>

          {/* Highest paying */}
          <section>
            <h2 className="text-2xl font-bold mb-1">Highest-paying occupations in America</h2>
            <p className="text-muted-foreground mb-4">
              Ranked by average annual wage, {DATA_YEAR} national data (BLS OEWS).
            </p>
            {renderRankingTable(highPaying, 'Avg. wage')}
          </section>

          <InContentAd />

          {/* No degree */}
          <section>
            <h2 className="text-2xl font-bold mb-1">Best-paying jobs without a degree</h2>
            <p className="text-muted-foreground mb-4">
              High-wage occupations that don't typically require a four-year degree, {DATA_YEAR} national data.
            </p>
            {renderRankingTable(noDegree, 'Avg. wage')}
            <div className="mt-3">
              <Button asChild variant="outline" size="sm">
                <Link to="/no-degree-jobs">See the full no-degree list <ArrowRight className="h-4 w-4 ml-1" /></Link>
              </Button>
            </div>
          </section>

          {/* Fastest growing */}
          <section>
            <h2 className="text-2xl font-bold mb-1">Fastest-growing & in-demand careers</h2>
            <p className="text-muted-foreground mb-4">Featured high-growth occupations from BLS Employment Projections.</p>
            <div className="grid sm:grid-cols-2 gap-4">
              {(trending || []).slice(0, 6).map((c, i) => (
                <Card key={`${c.soc_code || c.code}-${i}`} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-4 flex items-center justify-between">
                    <Link
                      to={generateSalaryPageUrl({ title: c.title || c.name || '', soc: c.soc_code || c.code }, 'united-states')}
                      className="font-medium hover:text-wage-primary"
                    >
                      {c.title || c.name}
                    </Link>
                    {c.growth && (
                      <Badge variant="secondary" className="bg-green-100 text-green-800">{c.growth}</Badge>
                    )}
                  </CardContent>
                </Card>
              ))}
              {(!trending || trending.length === 0) && (
                <p className="text-muted-foreground">Loading growth data...</p>
              )}
            </div>
          </section>

          {/* Methodology + citation */}
          <section className="rounded-lg border bg-muted/30 p-6 space-y-4">
            <h2 className="text-xl font-bold">Methodology &amp; sources</h2>
            <p className="text-sm text-muted-foreground">
              Wage figures come from the U.S. Bureau of Labor Statistics Occupational Employment and Wage
              Statistics (OEWS) program ({DATA_YEAR}). Growth signals draw on BLS Employment Projections.
              Cost-of-living adjustments, where shown elsewhere on Wage Atlas, use BEA Regional Price Parities.
              "Without a degree" reflects BLS typical entry-level education classifications. Figures are averages
              and medians; individual pay varies by employer, experience and metro area.
            </p>
            <div className="rounded-md bg-background border p-4">
              <div className="flex items-center gap-2 mb-2 text-sm font-medium">
                <Quote className="h-4 w-4 text-wage-primary" /> Cite this report
              </div>
              <p className="text-sm text-muted-foreground break-words">{citation}</p>
              <Button size="sm" variant="outline" className="mt-3" onClick={handleCopyCitation}>
                Copy citation
              </Button>
            </div>
          </section>

          {/* Share */}
          <section className="text-center space-y-3">
            <h2 className="text-xl font-bold flex items-center justify-center gap-2">
              <Share2 className="h-5 w-5" /> Share this report
            </h2>
            <div className="flex justify-center gap-3">
              <Button asChild variant="outline" size="sm">
                <a
                  href={`https://twitter.com/intent/tweet?text=${shareText}&url=${shareUrl}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Twitter className="h-4 w-4 mr-2" /> Share on X
                </a>
              </Button>
              <Button asChild variant="outline" size="sm">
                <a
                  href={`https://www.linkedin.com/sharing/share-offsite/?url=${shareUrl}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Linkedin className="h-4 w-4 mr-2" /> Share on LinkedIn
                </a>
              </Button>
            </div>
          </section>

          {/* Newsletter */}
          <NewsletterSignup
            source="state_of_wages_report"
            title="Get the next State of American Wages"
            description="We publish a fresh edition every quarter. Get it - plus the data behind it - in your inbox."
          />
        </div>
      </div>
    </>
  );
};

export default StateOfWages;
